import React, { useEffect } from 'react';
import AccountSetupCta from '../intake/AccountSetupCta';
import ConciergeOtoGate from '../intake/ConciergeOtoGate';
import DexaCreditsNote from '../intake/DexaCreditsNote';

/**
 * Post-submit panel for the TRT intake. Renders "what happens next" copy
 * tailored to the lab-routing path the patient took.
 *
 * Three lab-lifecycle paths:
 *   1. belugaOrdersLabs=true, labMethod=atHome   → kit ships
 *   2. belugaOrdersLabs=true, labMethod=inPerson → requisition PDF arrives
 *   3. belugaOrdersLabs=false                    → uploaded labs reviewed at consult
 *
 * In all three, sync consult comes after labs land (via bookingLink in the
 * LAB_ORDER_RESULTS webhook — see BACKEND_TRT_LAB_INTEGRATION_PRD.md §7.6).
 */
const TrtCompleteStep = ({
  STORAGE_KEY,
  answers,
  visitResult,
  belugaOrdersLabs,
  labMethod,
}) => {
  const contactInfo = answers?.['basic-contact'] || {};
  const firstName = (contactInfo.name || 'Patient').split(' ')[0];

  useEffect(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing saved progress:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine which lab-lifecycle panel to show.
  let panelTitle = "We've received your intake";
  let panelBody =
    "Your provider will review everything and reach out via your patient portal.";
  let nextSteps = [
    'A licensed provider will review your information within 24-48 hours',
    'You will receive updates via email and your patient portal',
    'Once labs are complete, you can book your video consult',
  ];

  if (belugaOrdersLabs && labMethod === 'atHome') {
    panelTitle = 'Your at-home lab kit is on its way';
    panelBody =
      "We're shipping you a lab collection kit in the next 1-3 business days. We'll email and text you tracking as soon as it ships.";
    nextSteps = [
      "We'll send tracking when your kit ships",
      'Complete the sample using the included instructions, then ship it back with the prepaid label',
      "When results land, you'll receive a booking link to schedule your video consult",
    ];
  } else if (belugaOrdersLabs && labMethod === 'inPerson') {
    panelTitle = 'Your lab requisition is on its way';
    panelBody =
      "We'll email you a lab requisition PDF within minutes. Take it to a Quest or LabCorp draw site at your convenience.";
    nextSteps = [
      "Watch your email for the lab requisition PDF (within minutes)",
      'Visit a Quest or LabCorp draw site at your convenience',
      "When results land, you'll receive a booking link to schedule your video consult",
    ];
  } else if (!belugaOrdersLabs) {
    panelTitle = "We've received your labs";
    panelBody =
      'Your provider will review your uploaded lab results during your video consult.';
    nextSteps = [
      'Your provider will review your uploaded lab results',
      "You'll receive a booking link to schedule your video consult",
      'During the consult, your provider will confirm your treatment plan',
    ];
  }

  const holdingStatus = visitResult?.holdingStatus;

  return (
    <ConciergeOtoGate answers={answers} visitType="trt" visitResult={visitResult}>
    <div data-testid="trt-complete-step" className="text-center">
      {/* Success icon */}
      <div className="w-20 h-20 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-6">
        <svg
          className="w-10 h-10 text-[#2d8a5f]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>

      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        Thanks, {firstName}!
      </h2>
      <p className="text-lg text-gray-600 mb-2 max-w-md mx-auto">
        Your TRT intake has been submitted. Here's what happens next.
      </p>
      {/* §preference-not-prescription: the plan chosen at checkout is a request.
          The provider reviews labs + intake and may prescribe a different
          formulation (e.g. cream instead of injection). */}
      <p className="text-sm text-gray-500 mb-8 max-w-md mx-auto">
        Your provider will prescribe the treatment that&rsquo;s right for you, which
        may differ from the option you selected.
      </p>

      {/* Visit reference (above the fold so smokes + support can capture
          it without scrolling). Falls back to a hidden state when masterId
          isn't available yet (offline / partial-failure paths). */}
      {visitResult?.masterId && (
        <div
          data-testid="trt-complete-masterid"
          className="bg-[#f0faf4] border border-[#2d8a5f]/30 rounded-2xl px-5 py-4 mb-8 max-w-md mx-auto text-left"
        >
          <div className="text-xs text-[#2d8a5f] uppercase tracking-wide mb-1 font-medium">
            Visit reference
          </div>
          <div className="text-lg font-mono font-semibold text-gray-900 select-all break-all">
            {visitResult.masterId}
          </div>
        </div>
      )}

      {/* Lab-lifecycle panel */}
      <div
        data-testid="trt-lab-panel"
        className="bg-[#fff7ed] border border-[#fed7aa] rounded-2xl p-6 mb-8 max-w-md mx-auto text-left"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-8 h-8 bg-[#fed7aa] rounded-full flex items-center justify-center flex-shrink-0">
            <svg
              className="w-5 h-5 text-[#c2410c]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{panelTitle}</h3>
            {holdingStatus && (
              <p className="text-sm text-[#c2410c] font-medium mt-0.5">
                Status: {holdingStatus.replace(/_/g, ' ')}
              </p>
            )}
          </div>
        </div>
        <p className="text-gray-700 text-sm">{panelBody}</p>
      </div>

      {/* What happens next */}
      <div className="bg-[#f0faf4] rounded-2xl p-6 mb-8 max-w-md mx-auto">
        <h3 className="font-semibold text-gray-900 mb-4">Next steps</h3>
        <ul className="space-y-3 text-left">
          {nextSteps.map((step, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="w-6 h-6 bg-[#2d8a5f] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">{i + 1}</span>
              </div>
              <span className="text-gray-700">{step}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Set password (new patients) + dashboard login */}
      <DexaCreditsNote answers={answers} />
      <AccountSetupCta email={contactInfo.email} />
    </div>
    </ConciergeOtoGate>
  );
};

export default TrtCompleteStep;
